import { useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { Sheet } from '@/components/ui/Sheet';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAccessAudit, type AccessPermission, type GrantState } from '@/api/bi/access';
import { PERMISSIONS } from './grants';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Full access change history.
 *
 * Previously this was eight unlabelled lines pinned to the bottom of the
 * settings page reading "<slug> changed for <role> by <actor>" — no timestamp,
 * and no indication of what the change actually was. Here each entry shows the
 * before/after state per permission, which is the only version of this that
 * answers "who took away my export button".
 */
export function AccessAuditSheet({
  open,
  onOpenChange,
  enabled,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  enabled: boolean;
}) {
  const audit = useAccessAudit(enabled && open);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const all = audit.data?.results ?? [];
    if (!needle) return all;
    return all.filter((row) => {
      const target = row.target_type === 'role' ? (row.target_role ?? '') : `user #${row.target_user}`;
      return (
        row.resource_slug.toLowerCase().includes(needle) ||
        target.toLowerCase().includes(needle) ||
        (row.actor ?? '').toLowerCase().includes(needle)
      );
    });
  }, [audit.data?.results, query]);

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Access change history"
      description="Every grant edit, newest first."
      width="lg"
    >
      <div className="space-y-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by resource, target or actor…"
          aria-label="Filter audit entries"
          className="h-8 text-xs"
        />

        {audit.isError ? (
          <ErrorState onRetry={() => void audit.refetch()} />
        ) : audit.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={query ? 'No matching changes' : 'No changes recorded yet'}
            description={
              query
                ? 'Try a different resource, role or actor.'
                : 'Grant edits will appear here as soon as someone saves one.'
            }
          />
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="rounded-md border border-border bg-surface px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span className="font-mono font-medium">{row.resource_slug}</span>
                  <Badge tone="neutral" className="capitalize">
                    {row.target_type === 'role' ? (row.target_role ?? 'role') : `user #${row.target_user}`}
                  </Badge>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {fmt.datetime(row.created_at)}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  by <span className="text-foreground">{row.actor ?? 'system'}</span>
                </p>
                <PermissionDiff before={row.before} after={row.after} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}

function PermissionDiff({
  before,
  after,
}: {
  before: Partial<Record<AccessPermission, GrantState>>;
  after: Partial<Record<AccessPermission, GrantState>>;
}) {
  const changed = PERMISSIONS.filter((p) => (before[p] ?? 'inherit') !== (after[p] ?? 'inherit'));
  if (changed.length === 0) {
    return <p className="mt-1 text-[10px] text-muted-foreground">No permission values changed.</p>;
  }
  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {changed.map((permission) => (
        <li
          key={permission}
          className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px]"
        >
          <span className="capitalize text-muted-foreground">{permission}</span>
          <StateChip state={before[permission] ?? 'inherit'} />
          <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" aria-hidden />
          <StateChip state={after[permission] ?? 'inherit'} />
        </li>
      ))}
    </ul>
  );
}

function StateChip({ state }: { state: GrantState }) {
  return (
    <span
      className={cn(
        'font-medium capitalize',
        state === 'allow' && 'text-success',
        state === 'deny' && 'text-danger',
        state === 'inherit' && 'text-muted-foreground',
      )}
    >
      {state}
    </span>
  );
}
